import React from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@components/Button";
import { BinIcon } from "@components/icons";
import { Modal } from "@components/Modal/Modal";
import { useApiErrorUtils } from "@hooks/useApiErrorUtils";
import { useModal } from "@hooks/useModal";
import { type IUseModalProps } from "@store/modal/modal";
import { useRemoveSavingsPlanMutation } from "@services/budget/mutations";
import { type ISavingsPlan } from "@services/budget/types";

interface IRemoveSavingsPlanModalProps extends IUseModalProps {
  savingsPlan: ISavingsPlan;
}

export const RemoveSavingsPlanModal: React.FC<IRemoveSavingsPlanModalProps> = (
  props,
) => {
  const { savingsPlan, ...rest } = props;
  const { t } = useTranslation();
  const modal = useModal(rest);

  const { mutateAsync: removeSaving, isPending: isRemovePending } =
    useRemoveSavingsPlanMutation();

  const { toastApiError } = useApiErrorUtils();

  const handleSubmit = async () => {
    const response = await removeSaving({
      savingsPlanId: savingsPlan.savingsPlanId,
    }).catch(toastApiError);

    if (response) {
      toast.success(t("budget.savings.editSavingsPlanModal.remove.success"));
      modal.close();
    }
  };

  return (
    <Modal
      modal={modal}
      className="md:max-w-xl"
      badgeProps={{ icon: BinIcon, color: "red" }}
      title={t("settings.budget.removeModal.title")}
      subtitle={savingsPlan.plan.planName}
      actionButtons={
        <React.Fragment>
          <Button className="w-full" variant="secondary" onClick={modal.close}>
            {t("settings.budget.removeModal.cancel")}
          </Button>
          <Button
            className="w-full"
            onClick={handleSubmit}
            isLoading={isRemovePending}
          >
            {t("settings.budget.removeModal.confirm")}
          </Button>
        </React.Fragment>
      }
    ></Modal>
  );
};
